import fp from 'lodash/fp';
import { cpdPointStatus, PublishedEventTypes } from '../../../../constants';
import { externalActivityType } from '../../../../constants/content-type';
import { isTeamManager } from '../../../../services';

const validateNonEventAndPastEvent = (type, timeEarned, eventType) => (eventType !== PublishedEventTypes.Course) && ((type !== externalActivityType.Event && type !== externalActivityType.Webinar) || ((type === externalActivityType.Event || type === externalActivityType.Webinar) && timeEarned <= new Date()))

const validateFutureEvent = (type, timeEarned, eventType) => (eventType !== PublishedEventTypes.Course) && (type === externalActivityType.Event) && (timeEarned > new Date())

const validateFutureWebinar = (type, timeEarned, eventType) => (eventType !== PublishedEventTypes.Course) && (type === externalActivityType.Webinar) && (timeEarned > new Date())

const validateCourse = (type, timeEarned, eventType) => eventType === PublishedEventTypes.Course

const validatePast = (type, timeEarned, eventType) => (timeEarned < new Date()) || !timeEarned

const validateFuture = (type, timeEarned, eventType) => (timeEarned > new Date())

const selfSelected = (users, self) => {
  
  const selfUser = fp.find(
    // When current user cannot create external cpd for others, the report users structure is [{ id: 'user id'}]
    // When current user can create external cpd for others, the report users structure is [{ user: { id: 'user id'}}]
    ({ user, id }) => (user ? user.id : id) === self.id
  )(users);

  return selfUser;
}

const selfSelectedOnly = (users, self) => {
  const isSelfSelected = selfSelected(users, self);

  return users.length === 1 && isSelfSelected;
}

const userAmountText = (userAmount) => {
  if (userAmount <= 0) {
    return 'Invalid user amount';
  }
  return userAmount === 1 ? ' 1 person' : ` ${userAmount} people`;
}

const memberUserAmountText = (userAmount) => {
  if (userAmount <= 0) {
    return 'Invalid user amount';
  }
  return userAmount === 1 ? ' 1 member' : ` ${userAmount} members`;
}

export const workflow = ({
  onSaveBusinessUserCPD,
  onSaveTeamManagerCPD,
  onUpdateBusinessUserCPD,
  onDeleteBusinessUserCPD,
  onSaveIndividualUserCPD,
  onUpdateIndividualUserCPD,
  onDeleteIndividualUserCPD,
  business,
  currentUser,
  details
}) => ({
  IndividualUser: [
    { show: !details.status, buttonName: 'Create', className: '', performAction: () => (onSaveIndividualUserCPD(cpdPointStatus.approved)), confirmation: undefined },
    { show: fp.includes(details.status)([cpdPointStatus.draft]), buttonName: 'Create', className: '', performAction: () => (onUpdateIndividualUserCPD(cpdPointStatus.approved)), confirmation: undefined },
    { show: !details.status, buttonName: 'Save as Draft', validType: validateNonEventAndPastEvent, className: '', performAction: () => (onSaveIndividualUserCPD(cpdPointStatus.draft)), confirmation: undefined },
    {
      show: !details.status, buttonName: 'Register for Event', validType: validateFutureEvent, className: '', performAction: () => (onSaveIndividualUserCPD(cpdPointStatus.registered)), confirmation: undefined
    },
    {
      show: !details.status, buttonName: 'Register for Webinar', validType: validateFutureWebinar, className: '', performAction: () => (onSaveIndividualUserCPD(cpdPointStatus.registered)), confirmation: undefined
    },
    {
      show: !details.status, buttonName: 'Register for Course', validType: validateCourse, className: '', performAction: () => (onSaveIndividualUserCPD(cpdPointStatus.registered)), confirmation: undefined
    },
    { show: fp.includes(details.status)([cpdPointStatus.registered]), buttonName: 'Record Attendance', className: '', performAction: () => (onUpdateIndividualUserCPD(cpdPointStatus.approved)), confirmation: undefined },
    { show: fp.includes(details.status)([cpdPointStatus.draft]), buttonName: 'Update Draft', checkReadOnly: true, className: '', performAction: () => (onUpdateIndividualUserCPD(details.status)), confirmation: undefined },
    { show: fp.includes(details.status)([cpdPointStatus.approved]), buttonName: 'Update', checkReadOnly: true, className: '', performAction: () => (onUpdateIndividualUserCPD(details.status)), confirmation: undefined },
    {
      show: fp.includes(details.status)([cpdPointStatus.registered]), readOnly: true, buttonName: 'I did not attend', validType: validatePast, className: 'btn-delete', performAction: () => (onUpdateIndividualUserCPD(cpdPointStatus.notAttending)), confirmation: {
        heading: 'Did not attend',
        header: ({ contentType }) => `Are you sure you want to mark this ${contentType} as not attended?`,
        body: ({ users, contentType }) => `By not attending this ${contentType}, any task that requires you attend will be failed.`,
        confirmLabel: 'Yes',
        cancelLabel: 'No',
        reason: false
      }
    },
    {
      show: fp.includes(details.status)([cpdPointStatus.registered]), readOnly: true, buttonName: 'I will not attend', validType: validateFuture, className: 'btn-delete', performAction: () => (onUpdateIndividualUserCPD(cpdPointStatus.notAttending)), confirmation: {
        heading: 'Will not attend',
        header: ({ contentType }) => `Are you sure you want to mark this ${contentType} as not attended?`,
        body: ({ users, contentType }) => `By not attending this ${contentType}, any task that requires you attend will be failed.`,
        confirmLabel: 'Yes',
        cancelLabel: 'No',
        reason: false
      }
    },
    {
      show: fp.includes(details.status)(
        [cpdPointStatus.approved, cpdPointStatus.draft, cpdPointStatus.registered, cpdPointStatus.notAttending]
      ), readOnly: true, buttonName: 'Delete', className: 'btn-delete', performAction: () => (onDeleteIndividualUserCPD()), confirmation: {
        heading: 'Delete External CPD',
        header: () => 'Are you sure you want to delete this External CPD activity?',
        body: ({ users }) => 'Once the activity is deleted, it cannot be undone.',
        confirmLabel: 'Yes, delete',
        cancelLabel: 'I\'ve changed my mind! Take me back',
        reason: false
      }
    }
  ],
  BusinessUser: [
    {
      show: !details.status, readOnly: false, buttonName: 'Submit for approval', validType: validateNonEventAndPastEvent, className: '', performAction: () => (onSaveIndividualUserCPD(cpdPointStatus.pending)), confirmation: {
        heading: 'Create External CPD',
        header: () => 'Are you sure you want to record this External CPD activity?',
        body: ({ users }) => 'You are about to submit an External CPD activity for approval. Your team administrators will be notified, and will review your request.',
        confirmLabel: 'Submit for approval',
        cancelLabel: 'No, revise activity',
        reason: false
      }
    },
    {
      show: fp.includes(details.status)([cpdPointStatus.registered, cpdPointStatus.draft]), readOnly: false, buttonName: 'Submit for approval', validType: validateNonEventAndPastEvent, className: '', performAction: () => (onUpdateIndividualUserCPD(cpdPointStatus.pending)), confirmation: {
        heading: 'Create External CPD',
        header: () => 'Are you sure you want to record this External CPD activity?',
        body: ({ users }) => 'You are about to submit an External CPD activity for approval. Your team administrators will be notified, and will review your request.',
        confirmLabel: 'Submit for approval',
        cancelLabel: 'No, revise activity',
        reason: false
      }
    },
    {
      show: !details.status, readOnly: false, buttonName: 'Register for Webinar', validType: validateFutureWebinar, className: '', performAction: () => (onSaveIndividualUserCPD(cpdPointStatus.registered)), confirmation: {
        heading: 'Register for Webinar',
        header: () => 'Are you sure you want to record this event?',
        body: () => 'Are you sure you want to record this event as one you have registered for? We\'ll send you a reminder after the event date to submit your CPD for approval.',
        confirm: 'Yes, register for Webinar',
        cancel: 'No, revise activity'
      }
    },
    {
      show: !details.status, readOnly: false, buttonName: 'Register for Event', validType: validateFutureEvent, className: '', performAction: () => (onSaveIndividualUserCPD(cpdPointStatus.registered)), confirmation: {
        heading: 'Register for Event',
        header: () => 'Are you sure you want to record this event?',
        body: () => 'Are you sure you want to record this event as one you have registered for? We\'ll send you a reminder after the event date to submit your CPD for approval.',
        confirm: 'Yes, register for event',
        cancel: 'No, revise activity'
      }
    },
    {
      show: !details.status, readOnly: false, buttonName: 'Register for Course', validType: validateCourse, className: '', performAction: () => (onSaveIndividualUserCPD(cpdPointStatus.registered)), confirmation: {
        heading: 'Register for Course',
        header: () => 'Are you sure you want to enroll in this course?',
        body: () => 'Are you sure you want to record this course as one you have registered for? We\'ll send you a reminder after the course compeletion date to submit your CPD for approval.',
        confirm: 'Yes, register for course',
        cancel: 'No, revise activity'
      }
    },
    { show: !details.status, buttonName: 'Save as Draft', validType: validateNonEventAndPastEvent, className: '', performAction: () => (onSaveIndividualUserCPD(cpdPointStatus.draft)), confirmation: undefined },
    {
      show: fp.includes(details.status)([cpdPointStatus.pending]), readOnly: false, buttonName: 'Submit for approval', className: '', performAction: () => (onUpdateIndividualUserCPD(cpdPointStatus.pending)), confirmation: {
        heading: 'Edit External CPD',
        header: () => 'Are you sure you want to resubmit this External CPD activity?',
        body: ({ users }) => 'You are about to resubmit an External CPD activity for approval. Your team administrators will be notified, and will review your request.',
        confirmLabel: 'Yes, resubmit',
        cancelLabel: 'No, revise activity',
        reason: false
      }
    },
    {
      show: fp.includes(details.status)([cpdPointStatus.draft]), readOnly: false, buttonName: 'Update', className: '', performAction: () => (onUpdateIndividualUserCPD(cpdPointStatus.draft)), confirmation: {
        heading: 'Edit External CPD',
        header: () => 'Are you sure you want to update this External CPD activity draft?',
        body: ({ users }) => 'You are about to update an External CPD activity draft',
        confirmLabel: 'Yes',
        cancelLabel: 'No, revise activity',
        reason: false
      }
    },
    {
      show: fp.includes(details.status)(
        [cpdPointStatus.pending, cpdPointStatus.draft, cpdPointStatus.registered, cpdPointStatus.notAttending]
      ), readOnly: true, buttonName: 'Delete', className: 'btn-delete', performAction: () => (onDeleteIndividualUserCPD()), confirmation: {
        heading: 'Delete External CPD',
        header: () => 'Are you sure you want to delete this External CPD activity?',
        body: ({ users }) => 'Once the activity is deleted, it cannot be undone.',
        confirmLabel: 'Yes, delete',
        cancelLabel: 'I\'ve changed my mind! Take me back',
        reason: false
      }
    },
    {
      show: fp.includes(details.status)([cpdPointStatus.registered]), readOnly: true, buttonName: 'I did not attend', validType: validatePast, className: 'btn-delete', performAction: () => (onUpdateIndividualUserCPD(cpdPointStatus.notAttending)), confirmation: {
        heading: 'Did not attend',
        header: ({ contentType }) => `Are you sure you want to mark this ${contentType} as not attended?`,
        body: ({ users, contentType }) => `By not attending this ${contentType}, any task that requires you attend will be failed.`,
        confirmLabel: 'Yes',
        cancelLabel: 'No',
        reason: false
      }
    },
    {
      show: fp.includes(details.status)([cpdPointStatus.registered]), readOnly: true, buttonName: 'I will not attend', validType: validateFuture, className: 'btn-delete', performAction: () => (onUpdateIndividualUserCPD(cpdPointStatus.notAttending)), confirmation: {
        heading: 'Will not attend',
        header: ({ contentType }) => `Are you sure you want to mark this ${contentType} as not attended?`,
        body: ({ users, contentType }) => `By not attending this ${contentType}, any task that requires you attend will be failed.`,
        confirmLabel: 'Yes',
        cancelLabel: 'No',
        reason: false
      }
    }
  ],
  BusinessAdmin: [
    {
      show: !isTeamManager(currentUser, business) && !details.status, readOnly: false, buttonName: 'Create', validType: validateNonEventAndPastEvent, className: '', performAction: () => (onSaveBusinessUserCPD(cpdPointStatus.approved)), confirmation: {
        heading: 'Create External CPD',
        header: () => 'Are you sure you want to record this External CPD activity?',
        body: ({ users, reviewUserName }) => `You are about to record approved External CPD activities for ${userAmountText(users.length)}.`,
        confirmLabel: 'Yes, create now',
        cancelLabel: 'No, revise activity',
        reason: false
      }
    },
    {
      show: isTeamManager(currentUser, business) && !details.status, readOnly: false, buttonName: 'Create', validType: validateNonEventAndPastEvent, className: '', performAction: () => (onSaveTeamManagerCPD(cpdPointStatus.pending, cpdPointStatus.approved)), confirmation: {
        heading: 'Create External CPD',
        header: () => `Are you sure you want to record this External CPD activity?`,
        body: () => {
          const users = details.users;

          // Not includes the team manager himself
          const memberUserAmount = selfSelected(users, currentUser) ? users.length - 1 : users.length;

          const memberUserMsg = memberUserAmount > 0 ? `You are about to approve and record this External CPD activity for ${memberUserAmountText(memberUserAmount)} in your team. ` : '';

          const teamManagerSelfMsg = selfSelected(users, currentUser) ? `Your activity will be submitted ${memberUserMsg !== '' ? 'separately' : ''} to your manager for approval and will sit as pending activity in your account until approved.` : '';

          return memberUserMsg + teamManagerSelfMsg;
        },
        confirmLabel: selfSelected(details.users, currentUser) ? 'SUBMIT FOR APPROVAL' : "Yes, create now",
        cancelLabel: 'Revise activity',
        reason: false
      }
    },
    {
      show: !details.status, readOnly: false, buttonName: 'Register for Event', validType: validateFutureEvent, className: '', performAction: () => (onSaveBusinessUserCPD(cpdPointStatus.registered)), confirmation: {
        heading: 'Register for Event',
        header: () => 'Are you sure you want to record this External CPD activity?',
        body: ({ users, reviewUserName }) => `You are about to register ${userAmountText(users.length)} for an event. They can submit the CPD for approval once they attend.`,
        confirmLabel: 'Yes, create now',
        cancelLabel: 'No, revise activity',
        reason: false
      }
    },
    {
      show: !details.status, readOnly: false, buttonName: 'Register for Webinar', validType: validateFutureWebinar, className: '', performAction: () => (onSaveBusinessUserCPD(cpdPointStatus.registered)), confirmation: {
        heading: 'Register for Webinar',
        header: () => 'Are you sure you want to record this External CPD activity?',
        body: ({ users, reviewUserName }) => `You are about to register ${userAmountText(users.length)} for an event. They can submit the CPD for approval once they attend.`,
        confirmLabel: 'Yes, create now',
        cancelLabel: 'No, revise activity',
        reason: false
      }
    },
    {
      show: !details.status, readOnly: false, buttonName: 'Register for Course', validType: validateCourse, className: '', performAction: () => (onSaveBusinessUserCPD(cpdPointStatus.registered)), confirmation: {
        heading: 'Register for Course',
        header: () => 'Are you sure you want to register for this course?',
        body: ({ users, reviewUserName }) => `You are about to register ${userAmountText(users.length)} for a course. They can submit the CPD for approval once they complete the course.`,
        confirmLabel: 'Yes, create now',
        cancelLabel: 'No, revise activity',
        reason: false
      }
    },
    { show: selfSelectedOnly(details.users, currentUser) && (!details.status || fp.includes(details.status)([cpdPointStatus.draft])) && !details.status, buttonName: 'Save as Draft', validType: validateNonEventAndPastEvent, className: '', performAction: () => (onSaveBusinessUserCPD(cpdPointStatus.draft)), confirmation: undefined },
    {
      show: fp.includes(details.status)([cpdPointStatus.registered]), readOnly: true, buttonName: 'I did not attend', validType: validatePast, className: 'btn-delete', performAction: () => (onUpdateBusinessUserCPD(cpdPointStatus.notAttending)), confirmation: {
        heading: 'Did not attend',
        header: ({ contentType }) => `Are you sure you want to mark this ${contentType} as not attended?`,
        body: ({ users, contentType }) => `By not attending this ${contentType}, any task that requires you attend will be failed.`,
        confirmLabel: 'Yes',
        cancelLabel: 'No',
        reason: false
      }
    },
    {
      show: fp.includes(details.status)([cpdPointStatus.registered]), readOnly: true, buttonName: 'I will not attend', validType: validateFuture, className: 'btn-delete', performAction: () => (onUpdateBusinessUserCPD(cpdPointStatus.notAttending)), confirmation: {
        heading: 'Will not attend',
        header: ({ contentType }) => `Are you sure you want to mark this ${contentType} as not attended?`,
        body: ({ users, contentType }) => `By not attending this ${contentType}, any task that requires you attend will be failed.`,
        confirmLabel: 'Yes',
        cancelLabel: 'No',
        reason: false
      }
    },
    {
      show: fp.includes(details.status)([cpdPointStatus.pending]), readOnly: false, buttonName: 'Approve', className: '', performAction: () => (onUpdateBusinessUserCPD(cpdPointStatus.approved)), confirmation: {
        heading: 'Approve External CPD',
        header: () => 'Are you sure you want to approve this External CPD activity?',
        body: ({ users, reviewUserName }) => `${reviewUserName} will be notified that their request has been approved.`,
        confirmLabel: 'Yes, approve',
        cancelLabel: 'No, revise activity',
        reason: false
      }
    },
    {
      show: fp.includes(details.status)([cpdPointStatus.pending]), readOnly: false, buttonName: 'Reject', className: 'btn-orange', performAction: (reason) => (onUpdateBusinessUserCPD(cpdPointStatus.draft, reason)), confirmation: {
        heading: 'Reject External CPD',
        header: () => 'Are you sure you want to reject this External CPD activity?',
        body: ({ users, reviewUserName }) => `${reviewUserName} will be notified that their request has been rejected.`,
        confirmLabel: 'Yes, reject',
        cancelLabel: 'No, revise activity',
        reason: true
      }
    },
    {
      show: fp.includes(details.status)([cpdPointStatus.approved, cpdPointStatus.draft]), readOnly: false, buttonName: 'Update', className: '', performAction: () => (onUpdateBusinessUserCPD(details.status)), confirmation: undefined
    },
    {
      show: fp.includes(details.status)(
        [cpdPointStatus.approved, cpdPointStatus.notAttending, cpdPointStatus.draft, cpdPointStatus.registered]
      ), readOnly: true, buttonName: 'Delete', className: 'btn-delete', performAction: () => (onDeleteBusinessUserCPD()), confirmation: {
        heading: 'Delete External CPD',
        header: () => 'Are you sure you want to delete this External CPD activity?',
        body: ({ users }) => 'Once the activity is deleted, it cannot be undone.',
        confirmLabel: 'Yes, delete',
        cancelLabel: 'I\'ve changed my mind! Take me back',
        reason: false
      }
    }
  ]
})


// WEBPACK FOOTER //
// ./src/containers/pages/app/external-cpd/workflowConfig.js